/**
 * Address Service
 * Handles address search and place details API calls
 */

import network from '../../Network/NetworkManager';

const APPURL = require('../../Constants/URLConstants');

/**
 * Search for addresses (Custom/Mapbox)
 * @param {string} searchKey - Search query
 * @returns {Promise<object>} - Response with addresses array
 */
export const searchAddress = async (searchKey) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addresses}/search?key=${encodeURIComponent(searchKey)}`,
      'GET',
      '',
      '',
      '',
    );

    if (response.status || response.addresses) {
      return response;
    }

    return { addresses: [] };
  } catch (error) {
    console.error('Error searching address:', error);
    return { addresses: [] };
  }
};

/**
 * Search for places (Google Places)
 * @param {string} searchKey - Search query
 * @param {string} sessionToken - Session token for Google Maps
 * @returns {Promise<object>} - Response with places array
 */
export const searchPlaces = async (searchKey, sessionToken) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addresses}/places_search?key=${encodeURIComponent(searchKey)}&session_token=${sessionToken}`,
      'GET',
      '',
      '',
      '',
    );

    if (response.status || response.addresses) {
      return response;
    }

    return { addresses: [] };
  } catch (error) {
    console.error('Error searching places:', error);
    return { addresses: [] };
  }
};

/**
 * Get place details (Google Maps only)
 * @param {string} placeId - Google Place ID
 * @param {string} sessionToken - Session token
 * @returns {Promise<object>} - Response with place details
 */
export const getPlaceDetails = async (placeId, sessionToken) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addresses}/place_detail?place_id=${placeId}&session_token=${sessionToken}`,
      'GET',
      '',
      '',
      '',
    );

    if (response.status || response.address) {
      return response;
    }

    return null;
  } catch (error) {
    console.error('Error getting place details:', error);
    return null;
  }
};

/**
 * Tradly Maps (map_type default_tp): Google through the Tradly maps service, no tenant key.
 * Returned in the same shapes as searchPlaces / getPlaceDetails so callers stay unchanged.
 */
export const searchTradlyPlaces = async (searchKey, sessionToken) => {
  try {
    const response = await network.networkCall(
      'v1/maps/autocomplete',
      'POST',
      JSON.stringify({input: searchKey, session_token: sessionToken}),
      '',
      '',
    );
    const places = response?.data?.data || [];
    return {
      status: !!response?.status,
      data: {
        addresses: places.map(place => ({
          place_id: place.place_id,
          description: place.description,
          formatted_address: place.description,
        })),
      },
    };
  } catch (error) {
    console.error('Error searching Tradly Maps places:', error);
    return {addresses: []};
  }
};

export const getTradlyPlaceDetails = async (placeId, sessionToken) => {
  try {
    const response = await network.networkCall(
      `v1/maps/places/${encodeURIComponent(placeId)}?session_token=${encodeURIComponent(sessionToken)}`,
      'GET',
      '',
      '',
      '',
    );
    const location = response?.data?.data;
    return location ? {status: true, address: {location}} : null;
  } catch (error) {
    console.error('Error getting Tradly Maps place details:', error);
    return null;
  }
};

/**
 * Reverse geocode coordinates to address
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Promise<object>} - Response with address
 */
export const reverseGeocode = async (latitude, longitude) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addresses}/reverseGeoCodingAddress?lat=${latitude}&lng=${longitude}`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error reverse geocoding:', error);
    return null;
  }
};

/**
 * Get user's current location using browser geolocation
 * @returns {Promise<object>} - Coordinates object
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};
